import type {Ref} from "vue";
import {computed, toRef} from "vue";

import {useTax} from "./use-tax";

// Minimal cart-like shape required for totals
export type CartLike = {
	price: {amount: string};
	promotionCode?: {promotion: {__typename: string}} | null | undefined;
	giftCard?:
		{remainingAmount?: {amount?: string | null} | null} | null | undefined;
	products: Array<{
		price: {amount: string};
		discounts: Array<{amount: {amount: string}}>;
		quantity: number;
		product: {
			pricing: {
				originalPrice?: {amount?: string | null} | null;
				taxClass: {rate?: number | null};
			};
		};
	}>;
};

type CartProduct = CartLike["products"][number];

export function useCartTotals(cartInput: CartLike | Readonly<Ref<CartLike>>) {
	const cart = toRef(cartInput);
	const {calculateZeroTax} = useTax();

	const taxRate = computed<number>(
		() => cart.value.products[0]?.product.pricing.taxClass.rate ?? 0,
	);

	// Product totals without discounts
	const productsOriginalTotal = computed<number>(
		() =>
			cart.value.products.reduce(
				(accumulator: number, item: CartProduct) =>
					accumulator +
					Number(item.product.pricing.originalPrice?.amount ?? "0") *
						item.quantity,
				0,
			) || 0,
	);

	// Sum of product discounts - computed by comparing discounted and original prices
	const productDiscountsTotal = computed<number>(() =>
		cart.value.products.reduce((accumulator: number, item: CartProduct) => {
			const originalTotal =
				Number(item.product.pricing.originalPrice?.amount ?? "0") *
				item.quantity;
			const currentPrice = Number(item.price.amount);

			return accumulator + (originalTotal - currentPrice);
		}, 0),
	);

	// Cart's total with cart-level discounts and product-level discounts taken into account, but no gift card usages
	const cartTotal = computed<number>(() => Number(cart.value.price.amount));

	// Sum of product effective (discounted) prices
	const productsTotal = computed<number>(() =>
		cart.value.products.reduce(
			(accumulator, item) => accumulator + Number(item.price.amount),
			0,
		),
	);

	// The difference between cart items and the cart's computed total gives us our cart-level discounts
	// TODO: We would be better off receiving & reading this from the API, but this was done as a quick fix with minimum chance of breaking anything
	const cartLevelDiscounts = computed(
		() => productsTotal.value - cartTotal.value,
	);

	const discountsTotal = computed(
		() => productDiscountsTotal.value + cartLevelDiscounts.value,
	);

	// Determine preliminary applied gift card amount
	const giftCardAppliedAmount = computed<number>(() => {
		const remainingAmount = Number(
			cart.value.giftCard?.remainingAmount?.amount ?? "0",
		);
		const applied = Math.min(
			Math.max(0, remainingAmount),
			Math.max(0, cartTotal.value),
		);
		return Number(applied.toFixed(2));
	});

	const finalTotal = computed<number>(() => {
		const totalAfterGiftCards = cartTotal.value - giftCardAppliedAmount.value;
		return Number(totalAfterGiftCards.toFixed(2));
	});

	const taxlessAmount = computed<number>(() =>
		calculateZeroTax(finalTotal.value, taxRate.value),
	);

	const isFreeShipping = computed<boolean>(() => {
		if (!cart.value.promotionCode) {
			return false;
		}
		return (
			cart.value.promotionCode.promotion.__typename ===
			"CartPromotionFreeShipping"
		);
	});

	return {
		taxRate,
		productsOriginalTotal,
		productDiscountsTotal,
		discountsTotal,
		giftCardAppliedAmount,
		finalTotal,
		taxlessAmount,
		isFreeShipping,
	};
}
